import { Octokit } from '@octokit/core';

const octokit = new Octokit({ auth: process.env.GITHUB_AUTH_TOKEN });

export enum ArticleTypes {
  COMMUNIQATE = 'communiqate',
  MARQTING = 'marqting',
  ALGEMEEN = 'algemeen',
}

export interface Category {
  name: string;
  slug: string;
  description: string;
  icon: string;
  restricted: boolean;
}

export interface CategoryMetaData {
  name: string;
  description: string;
  icon: string;
  restricted: boolean;
}

export interface Article {
  content?: string;
  path: string;
  metadata: ArticleMetaData;
}

export interface ArticleMetaData {
  title: string;
  short_description?: string;
  image: string;
  author: string;
  slug: string;
  type?: ArticleTypes;
  created_at?: string;
  updated_at?: string;
}

export async function fetchCategories(): Promise<Category[]> {
  const rootFiles = await octokit
    .request(`GET /repos/${process.env.GITHUB_REPO_PATH}/contents`)
    .then((response) => response.data)
    .catch((err) => {
      console.error('Error fetching blog repo content', err);
      return [];
    });

  const categoryDirs = rootFiles.filter((file) => file.type === 'dir');
  const categories: Category[] = [];

  for (const dir of categoryDirs) {
    const fetchCategoryResponse = await octokit.request(
      `GET /repos/${process.env.GITHUB_REPO_PATH}/contents/{path}`,
      {
        path: dir.path,
      }
    );

    if (fetchCategoryResponse.status !== 200) {
      console.error('Error fetching category contents', {
        category: dir,
        response: fetchCategoryResponse,
      });
      continue;
    }

    const categoryFiles = fetchCategoryResponse.data;

    const metadata = categoryFiles.find(
      (categoryFile) => categoryFile.name === 'metadata.json'
    );
    const fetchMetadataResponse = await fetch(metadata.download_url);

    if (!fetchMetadataResponse.ok) {
      console.error('Error fetching metadata for category', {
        category: dir,
        response: fetchMetadataResponse,
      });
      continue;
    }

    const content: CategoryMetaData = await fetchMetadataResponse.json();

    categories.push({
      name: content.name || 'Geen naam bekend',
      slug: dir.path,
      description: content.description || '',
      icon: content.icon || '',
      restricted: content.restricted || false,
    });
  }
  return categories;
}

export async function fetchCategory(slug: string): Promise<Category> {
  const response = await octokit.request(
    `GET /repos/${process.env.GITHUB_REPO_PATH}/contents/{path}`,
    {
      path: slug,
    }
  );
  const categoryDir = response.data;
  const metadata = categoryDir.find(
    (categoryFile) => categoryFile.name === 'metadata.json'
  );
  const fetchMetadataResponse = await fetch(metadata.download_url);

  if (!fetchMetadataResponse.ok) {
    console.error('Error fetching metadata for category', {
      category: categoryDir,
      response: fetchMetadataResponse,
    });
    throw new Error('Error fetching category');
  }

  const content: CategoryMetaData = await fetchMetadataResponse.json();
  return {
    name: content.name || 'Geen naam bekend',
    slug: slug,
    description: content.description || '',
    icon: content.icon || '',
    restricted: content.restricted || false,
  };
}

/**
 * Fetch all articles
 */
export async function fetchArticles(): Promise<Article[]> {
  const categories: Category[] = await fetchCategories();
  let articles: Article[] = [];

  for (const category of categories) {
    const categoryArticles = await fetchCategoryArticles(category.slug);

    articles = articles.concat(categoryArticles);
  }

  return articles;
}

/**
 * Fetch articles for a given category slug
 * @param categorySlug
 */
export async function fetchCategoryArticles(
  categorySlug: string
): Promise<Article[]> {
  const articles: Article[] = [];
  const response = await octokit.request(
    `GET /repos/${process.env.GITHUB_REPO_PATH}/contents/{path}`,
    {
      path: categorySlug,
    }
  );
  const files = response.data;
  const articleDirs = files.filter((file) => file.type === 'dir');

  for (const articleDir of articleDirs) {
    const fetchArticleResponse = await octokit.request(
      `GET /repos/${process.env.GITHUB_REPO_PATH}/contents/{path}`,
      {
        path: articleDir.path,
      }
    );

    const articleDirFiles = fetchArticleResponse.data;

    const metadataFile = articleDirFiles.find(
      (articleDirFile) => articleDirFile.name === 'metadata.json'
    );
    const fetchMetadataResponse = await fetch(metadataFile.download_url);

    if (!fetchMetadataResponse.ok) {
      console.error('Error fetching metadata for category', {
        article: articleDir.name,
        response: fetchMetadataResponse,
      });
      throw new Error('Error fetching category');
    }

    const metadata: ArticleMetaData = await fetchMetadataResponse.json();

    articles.push({
      path: articleDir.path,
      metadata: {
        slug: metadata.slug,
        title: metadata.title,
        author: metadata.author,
        image: metadata.image,
        short_description: metadata.short_description,
        type: metadata.type,
        created_at: metadata.created_at,
        updated_at: metadata.updated_at,
      },
    });
  }

  return articles;
}

export async function fetchArticle(path: string): Promise<Article> {
  const fetchArticleResponse = await octokit.request(
    `GET /repos/${process.env.GITHUB_REPO_PATH}/contents/{path}`,
    {
      path: path,
    }
  );
  const articleDirFiles = fetchArticleResponse.data;

  const metadataFile = articleDirFiles.find(
    (articleDirFile) => articleDirFile.name === 'metadata.json'
  );
  const fetchMetadataResponse = await fetch(metadataFile.download_url);

  if (!fetchMetadataResponse.ok) {
    console.error('Error fetching metadata for category', {
      article: path,
      response: fetchMetadataResponse,
    });
    throw new Error('Error fetching category');
  }

  const metadata: ArticleMetaData = await fetchMetadataResponse.json();

  const indexFile = articleDirFiles.find(
    (articleDirFile) => articleDirFile.name === 'index.md'
  );
  const fetchIndexResponse = await fetch(indexFile.download_url);

  if (!fetchIndexResponse.ok) {
    console.error('Error fetching index file for article', {
      article: path,
      response: fetchIndexResponse,
    });
    throw new Error('Error article index file');
  }

  const content = await fetchIndexResponse.text();

  return {
    content: content,
    path: path,
    metadata: {
      slug: metadata.slug,
      title: metadata.title,
      author: metadata.author,
      image: metadata.image,
      short_description: metadata.short_description,
      created_at: metadata.created_at,
      updated_at: metadata.updated_at,
      type: metadata.type,
    },
  };
}
